import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import withLayout from "../layout";
import { searchLocations } from "../rent/api";
import { euro, isSubscription, locale } from "../rent/tariffs";
import { tariffName } from "../rent/ui";
import RentHeader, { RentFooter } from "../rent/RentHeader";
import SearchForm from "../rent/SearchForm";
import { distanceKm, formatKm, RADIUS_OPTIONS } from "../rent/places";
import "../login/login.css";


const parsePlace = (raw) => {
  try {
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

// Angefangene Stunden zwischen zwei Zeitpunkten
export const hoursBetween = (from, until) => {
  const ms = new Date(until) - new Date(from);
  return ms > 0 ? Math.ceil(ms / 36e5) : 0;
};

const PinIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 21s-7-6.2-7-11.5A7 7 0 0119 9.5C19 14.8 12 21 12 21z" />
    <circle cx="12" cy="9.5" r="2.5" />
  </svg>
);

const SearchPage = (props) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [parkings, setParkings] = useState({ isLoading: false, data: [] });
  const { setLoaderBox } = props;

  const query = Object.fromEntries(searchParams.entries());
  const place = parsePlace(query.parkingAt);
  const isHourly = query.activeTab !== "parking-monthly" && query.activeTab !== "parking-night";
  const isNight = query.activeTab === "parking-night";
  const radius = Number(query.radius) || 2;
  const hours = isHourly ? hoursBetween(query.parkingFrom, query.parkingUntil) : 0;
  const [activeId, setActiveId] = useState(null);
  const [refresh, setRefresh] = useState(0);

  const mapEl = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);
  const markersRef = useRef({});

  useEffect(() => {
    document.title = "Parkplatz finden | innopark24";
  }, []);

  // Suche an die API: Suchpunkt, Zeitraum und Umkreis in km
  const searchKey = searchParams.toString();
  useEffect(() => {
    if (!place) return;
    const params = { lat: place.lat, lng: place.lng, activeTab: query.activeTab, parkingFrom: query.parkingFrom, radius };
    if (isHourly || isNight) params.parkingUntil = query.parkingUntil;
    else params.duration = query.duration;
    let alive = true;
    setParkings((x) => ({ ...x, isLoading: true }));
    searchLocations(params)
      .then((data) => alive && setParkings({ isLoading: false, data }))
      .catch(() => alive && setParkings({ isLoading: false, data: [] }));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchKey, refresh]);

  useEffect(() => {
    setLoaderBox(parkings.isLoading);
  }, [parkings.isLoading, setLoaderBox]);

  // Entfernung berechnen, auf den Umkreis begrenzen, nach Entfernung sortieren
  const results = useMemo(() => {
    const list = Array.isArray(parkings.data) ? parkings.data : [];
    if (!place) return [];
    return list
      .filter((p) => p && p.lat != null && p.lng != null && p.tariffs.length > 0)
      .map((p) => {
        // Günstigster passender Tarif als "ab"-Preis (Abos nur, wenn monatlich gesucht wird)
        const wanted = query.activeTab === "parking-monthly" ? p.tariffs.filter(isSubscription) : p.tariffs.filter((x) => !isSubscription(x));
        const pool = wanted.length ? wanted : p.tariffs;
        const cheapest = pool.reduce((a, b) => (b.price < a.price ? b : a), pool[0]);
        return { ...p, cheapest, distance: distanceKm(place, { lat: p.lat, lng: p.lng }) };
      })
      .filter((p) => p.distance <= radius)
      .sort((a, b) => a.distance - b.distance);
  }, [parkings.data, place, radius, query.activeTab]);

  // Karte (Leaflet + OpenStreetMap) einmal anlegen
  useEffect(() => {
    if (!mapEl.current || mapRef.current || !place) return;
    const map = L.map(mapEl.current, { scrollWheelZoom: false }).setView([place.lat, place.lng], 14);
    L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a>-Mitwirkende',
    }).addTo(map);
    mapRef.current = map;
    layerRef.current = L.layerGroup().addTo(map);
    return () => {
      map.stop();
      map.off();
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [place && place.lat, place && place.lng]);

  // Suchpunkt, Umkreis und Parkplätze einzeichnen
  useEffect(() => {
    const map = mapRef.current;
    const layer = layerRef.current;
    if (!map || !layer || !place) return;
    layer.clearLayers();
    markersRef.current = {};
    const circle = L.circle([place.lat, place.lng], { radius: radius * 1000, color: "#1e9ff2", weight: 1.5, fillOpacity: 0.06 }).addTo(layer);
    L.marker([place.lat, place.lng], {
      icon: L.divIcon({ className: "rp-you-icon", html: '<span class="rp-you"></span>', iconSize: [16, 16], iconAnchor: [8, 8] }),
      title: place.location,
    }).addTo(layer);
    results.forEach((p) => {
      const marker = L.marker([Number(p.lat), Number(p.lng)], {
        icon: L.divIcon({ className: "rp-pin-icon", html: `<span class="rp-pin">${euro(p.cheapest.price, lang)}</span>`, iconSize: null, iconAnchor: [0, 0] }),
        title: p.title,
      })
        .addTo(layer)
        .on("click", () => setActiveId(p.id));
      markersRef.current[p.id] = marker;
    });
    map.fitBounds(circle.getBounds(), { padding: [10, 10], animate: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [results, place, radius]);

  // Aktiven Parkplatz in der Karte hervorheben
  useEffect(() => {
    Object.entries(markersRef.current).forEach(([id, m]) => {
      const el = m.getElement();
      if (el) el.classList.toggle("is-active", String(id) === String(activeId));
    });
  }, [activeId, results]);

  const focusParking = (p) => {
    setActiveId(p.id);
    if (mapRef.current) mapRef.current.panTo([p.lat, p.lng]);
  };

  // Erneut suchen – auch bei gleichen Angaben (z. B. um neue freie Plätze zu sehen)
  const onSearch = (params) => {
    navigate(`/search?${new URLSearchParams(params).toString()}`, { replace: true });
    setRefresh((n) => n + 1);
  };

  const widenTo = RADIUS_OPTIONS.find((r) => r > radius);
  const widen = () => onSearch({ ...query, radius: String(widenTo) });

  // Zur Standortseite – Suchzeitraum wird dort für die Buchung übernommen
  const openSite = (p) => {
    const params = { activeTab: query.activeTab || "", parkingFrom: query.parkingFrom || "", parkingUntil: query.parkingUntil || "", from: searchKey };
    if (query.here) params.qr = "1";
    navigate(`/p/${encodeURIComponent(p.id)}?${new URLSearchParams(params).toString()}`);
  };
  const dt = (iso, opts) => new Date(iso).toLocaleString(locale(lang), opts);

  const period = !query.parkingFrom
    ? ""
    : isHourly
    ? `${dt(query.parkingFrom, { weekday: "short", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })} – ${dt(query.parkingUntil, { hour: "2-digit", minute: "2-digit" })} · ${hours} ${t("hours")}`
    : isNight
    ? `${t("rxTabNight")} · ${dt(query.parkingFrom, { weekday: "short", day: "2-digit", month: "2-digit", year: "numeric" })}`
    : `${t("rpFrom")} ${dt(query.parkingFrom, { day: "2-digit", month: "2-digit", year: "numeric" })} · ${query.duration === "Mon-Fri" ? t("rpMonFri") : t("rpMonSun")}`;

  return (
    <div className="rp">
      <RentHeader />
      <main className="rp-main">
        <SearchForm key={searchKey} initial={query} compact onSearch={onSearch} submitLabel={t("search")} />

        <div className="rp-results-head">
          <h1>{t("rpResultsHead", { count: results.length, radius })}</h1>
          <span>{period}</span>
        </div>

        <div className="rp-grid">
          <div className="rp-list">
            {results.map((p) => (
              <article
                key={p.id}
                className={`rp-item ${String(activeId) === String(p.id) ? "is-active" : ""}`}
                onMouseEnter={() => setActiveId(p.id)}
                onClick={() => focusParking(p)}
              >
                <div className="rp-thumb">{p.images[0] ? <img src={p.images[0]} alt="" /> : <span>P</span>}</div>
                <div className="rp-item-body">
                  <h2>{p.title}</h2>
                  <p className="rp-addr">
                    <PinIcon />
                    {p.address}
                  </p>
                  <div className="rx-tags">
                    <span className="rp-dist">{t("rpDistance", { distance: formatKm(p.distance) })}</span>
                    {p.tariffs.map((x) => (
                      <span key={x.id} className={`rx-chip ${x.type === "night" || x.type === "nightsub" ? "night" : "blue"}`}>
                        {tariffName(x, t)}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="rp-price">
                  <span className="rx-from">{t("rpFrom")}</span>
                  <strong>{euro(p.cheapest.price, lang)}</strong>
                  <span>{t(`rxU_${p.cheapest.type}`)}</span>
                  <button
                    className="ipl-btn rp-book"
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      openSite(p);
                    }}
                  >
                    <span>{t("rxChoose")}</span>
                  </button>
                </div>
              </article>
            ))}

            {!parkings.isLoading && results.length === 0 && (
              <div className="rp-none">
                <strong>{t("rpNoneHead", { radius })}</strong>
                <span>{t("rpNoneText")}</span>
                <div className="ipl-help-btns">
                  {widenTo && (
                    <button type="button" className="ipl-pill soft" onClick={widen}>
                      {t("rpWiden", { radius: widenTo })}
                    </button>
                  )}
                  <a className="ipl-pill line" href="https://innopark24.com/autofahrer/">
                    {t("rpRequest")}
                  </a>
                </div>
              </div>
            )}

            {results.length > 0 && (
              <div className="rp-empty">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="9" />
                  <path d="M12 8v5M12 16h.01" />
                </svg>
                <p>
                  <strong>{t("rpNotRightHead")}</strong>
                  <span>{t("rpNotRightText")}</span>
                </p>
                <a className="ipl-pill line" href="https://innopark24.com/autofahrer/">
                  {t("rpRequest")}
                </a>
              </div>
            )}
          </div>

          <div className="rp-map">
            <div ref={mapEl} className="rp-map-canvas" aria-label={t("rpMap")}></div>
          </div>
        </div>
      </main>
      <RentFooter />
    </div>
  );
};

export default withLayout(SearchPage);
